import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCRefHierarchicalTree, RCCServicesApi, RCCTooltip, toggleExpandedForAll } from "react-common-components";
import { useForm } from "react-hook-form";
import DropdownWithBackground from "src/components/DropdownWithBackground";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { PermissionEnum } from "src/context/Auth/interface";
import permissionsMenu, { lastLevelPermissionsPathWithoutSystemConfigs } from "src/context/Auth/permissionsMenu";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import HierarchyNode from "../HierarchyNode";
import { IHierarchyComponent, IRefHierarchyComponent } from "./interface";

const Hierarchy: React.ForwardRefRenderFunction<IRefHierarchyComponent, IHierarchyComponent> = ({
	permissionsData,
	errorLoading,
	loading,
	selectedOption,
	reloadHierarchy
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();
	const refHierarchicalTree = useRef<RCCRefHierarchicalTree>(null);
	const [hierarchy, setHierarchy] = useState<any[]>(permissionsMenu.filter((x) => x.efb001Codigo !== "systemConfigs").map((x) => ({
		...x,
		children: x.children?.filter((child) => !child.internalAction)
	})));
    
	const { register, control, handleSubmit, reset } = useForm();
	const [expanded, setExpanded] = useState(false);
	const expandAll = (newHierarchy?: any) => {
		const expandedData = toggleExpandedForAll({treeData: newHierarchy && Array.isArray(newHierarchy) ? [...newHierarchy] : [...hierarchy], expanded: !expanded});
		setHierarchy(expandedData);
		setExpanded(!expanded);
	};
	const compressAll = async (newHierarchy?: any) => {
		const expandedData = toggleExpandedForAll({treeData: newHierarchy && Array.isArray(newHierarchy) ? [...newHierarchy] : [...hierarchy], expanded: false});
		setHierarchy(expandedData);
		setExpanded(false);
	};

	const changeChildrenSelect = async (efb001Codigo: string, value: string) => {
		let newHierarchy = [...hierarchy];
		newHierarchy = newHierarchy.map((x: any) => {
			if(x.efb001Codigo === efb001Codigo){
				return {
					...x,
					permissionDefaultValue: value,
					permission: value,
					expanded: true,
					children: x.children.map((y: any) => ({
						...y,
						permissionDefaultValue: value,
						permission: value
					}))
				};
			}

			return {...x};
		});
		await compressAll(newHierarchy);
		reset();
		setHierarchy(newHierarchy);
	};
	const getNodeKeyFunction = ({node}: any) => node.children && node.children.length > 0 ? `remove_${node.efb001Codigo}` : node.efb001Codigo;  			
	
	const HierarchicalTree = useMemo(() => <RCCHierarchicalTree
		data={hierarchy}
		styleInline="width: 100%; .rst__tree{ height: 100%; padding-bottom: 1rem; }"
		ref={refHierarchicalTree}
		isSearchable={{
			searchKeyOfNode: "efb005Descricao",
			placeholderInput: translate("Pesquisar")
		}}
		getNodeKey={getNodeKeyFunction}
		generateNodeProps={(row:any) => ({
			title: <HierarchyNode 
				node={row.node}
				parentNode={row.parentNode}
				control={control}
				register={register}
				permissionsData={permissionsData}
				changeChildrensSelect={changeChildrenSelect}
			/>
		})}
		canDrop={() => false}
		canDrag={() => false}
	/>, [hierarchy, permissionsData]);

	const submit = async (dt: any) => {
		try{
			let permissionsDt = Object.keys(dt).filter((key) => !key.includes("remove_")).map((key) => dt[key]);
			permissionsDt = permissionsDt.filter((pData) => pData.permission != null).map((pData) => ({
				...pData,
				permission: pData.permission === "" ? -1 : Number(pData.permission),
				id: pData.id && pData.id !== null ? pData.id : null
			}));
			await RCCServicesApi.postRequest({
				method: "Permissions/InsertPermissions",
				body: {
					permissions: permissionsDt,
					efa005Id: selectedOption?.type === "users" ? Number(selectedOption.value) : null,
					efa004Id: selectedOption?.type === "usersGroups" ? Number(selectedOption.value) : null
				}
			});
			addToast({
				color: "green",
				description: translate("Permissões inseridas com sucesso"),
				title: translate("Sucesso")
			});
			addToast({
				color: "blue",
				description: translate("As novas permissões serão aplicadas no próximo login"),
				title: translate("Atenção")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao inserir permissões"), addToast);
		}
	};

	const deleteAll = () => {
		if(!selectedOption)
			return;

		confirm(
			translate("Exclusão de permissões"),
			`${translate("Deseja realmente excluir as permissões do")} ${selectedOption.label} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Permissions/Delete",
					urlParameters: {
						efa005Id: selectedOption?.type === "users" ? Number(selectedOption.value) : "",
						efa004Id: selectedOption?.type === "usersGroups" ? Number(selectedOption.value) : ""
					}
				});
		
				addToast({
					color: "green",
					description: translate("Permissões excluídas com sucesso"),
					title:translate("Sucesso")
				});

				addToast({
					color: "blue",
					description: translate("As novas permissões serão aplicadas no próximo login"),
					title: translate("Atenção")
				});
				
				reloadHierarchy(selectedOption.value, selectedOption.type, selectedOption.label);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir permissões"), addToast);
			},
			true
		);
	};

	const changeToAll = (permission: PermissionEnum) => {
		if(!selectedOption)
			return;	

		confirm(
			translate("Atualizar permissões"),
			`${translate("Deseja realmente atualizar as permissões do")} ${selectedOption.label} ?`,
			async () => {
				await RCCServicesApi.putRequest({
					method: "Permissions/Update",
					body: {
						permission,
						efa005Id: selectedOption?.type === "users" ? Number(selectedOption.value) : null,
						efa004Id: selectedOption?.type === "usersGroups" ? Number(selectedOption.value) : null,
						permissionsPaths: lastLevelPermissionsPathWithoutSystemConfigs
					}
				});
			
				addToast({
					color: "green",
					description: translate("Permissões atualizadas com sucesso"),
					title:translate("Sucesso")
				});

				addToast({
					color: "blue",
					description: translate("As novas permissões serão aplicadas no próximo login"),
					title: translate("Atenção")
				});
					
				reloadHierarchy(selectedOption.value, selectedOption.type, selectedOption.label);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar permissões"), addToast);
			},
			true
		);
	};

	useImperativeHandle(ref, () => ({
		compress: compressAll,
		reset: () => {
			reset();
			setHierarchy(permissionsMenu.filter((x) => x.efb001Codigo !== "systemConfigs").map((x) => ({
				...x,
				children: x.children?.filter((child) => !child.internalAction)
			})));
		}
	}));

	if(loading)
		return <Loader />;

	if(errorLoading)
		return <RCCAlert
			title={translate("Atenção")}
			description={translate("Selecione um grupo de usuário ou usuário para gerenciar as permissões")}
		/>;

	return (
		<>
			<RCCGrid container item justifyContent="space-between" alignItems="center">
				<Title
					color="purple"
				>
					{translate("Hierarquia de permissões")}
				</Title>
				<RCCGrid 
					container 
					item 
					xs={6}
					justifyContent="flex-end"	
				>
					<RCCTooltip
						color="grey"
						text={translate("Expandir/Comprimir")}
					>
						<RCCButton 
							color="transparent"
							icon={{
								name: expanded ? "compress" : "expand",
								color: "var(--default-color-font)",
								size: 16
							}}
							onClick={expandAll}
						/>
					</RCCTooltip>
					<RCCTooltip
						color="grey"
						text={translate("Pesquisar")}
					>
						<RCCButton 
							color="transparent"
							icon={{
								name: "search",
								color: "var(--default-color-font)",
								size: 16
							}}
							onClick={() => refHierarchicalTree.current?.toggleSearch()}
						/>
					</RCCTooltip>
					<DropdownWithBackground
						callerComponent={
							<RCCButton 
								color="purple"
								icon={{
									name: "arrowDown",
									size: 16
								}}
							>
								{translate("Aplicar para todos")}
							</RCCButton>
						}
					>
						<RCCDropDownItem
							selfEnclose
							onClick={() => changeToAll(PermissionEnum.VIEW)}
						>
							{translate("Visualiza")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => changeToAll(PermissionEnum.CREATE_UPDATE)}
						>
							{translate("Cria e edita")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => changeToAll(PermissionEnum.CREATE_UPDATE_DELETE)}
						>
							{translate("Cria, edita e exclui")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => changeToAll(PermissionEnum.NO_ACCESS)}
						>
							{translate("Sem acesso")}
						</RCCDropDownItem>
					</DropdownWithBackground>
					<RCCTooltip
						color="red"
						text={translate("Excluir todas")}
					>
						<RCCButton 
							color="red"
							icon={{
								name: "trash"
							}}
							onClick={deleteAll}
						/>
					</RCCTooltip>
				</RCCGrid>
			</RCCGrid>					
			
			{HierarchicalTree}

			<RCCGrid container item alignItems="center" justifyContent="flex-end">
				<RCCButton 
					color="green"
					onClick={handleSubmit(submit)}
				>
					{translate("Confirmar")}
				</RCCButton>
			</RCCGrid>
		</>
	);
};

export default forwardRef(Hierarchy);
